import {
	GuideMediaType,
	MediaGuideUIProps
} from "../components/VideoGuide/interface";
import { Modules, Stages, Steps } from "../contracts";
import {
	GuideUIProps,
	PopupInputProps
} from "@organisms/ZOnboarding/ZGuideUI/interface";
import React, { ReactElement } from "react";
import { CongratulationGuideUIProps } from "@/Onboarding/components/CongratulationGuide/interface";
import AlignCongratulationsFlash from "../assets/AlignCongratulationsFlash.svg";

export enum GuideType {
	POPUP = "popup",
	NORMAL = "normal",
	VIDEO = "video",
	POPUP_MODAL = "popup_modal"
}
export interface Guide {
	id: Steps;
	type: GuideType;
	guideUIProps: GuideUIProps | MediaGuideUIProps | CongratulationGuideUIProps;
	moduleComplete?: boolean;
	stageComplete?: boolean;
	triggerAPI?: boolean;
	isEntityRequired?: boolean;
}
export interface GuideHookProps {
	stageId: Stages | null;
	stepId: Steps;
	entityId?: string;
	popupInputProps?: PopupInputProps;
	handleNextButton?: (value?: string) => Promise<void>;
	highlightQuery?: string;
}

export interface GuideHookDetail {
	GuideUI: React.ElementType | null;
	resetGivenStep: (module: Modules, stage: Stages, step: Steps) => void;
	completeActiveStep: () => Promise<void>;
	handleGuideVisiblity?: (value: boolean) => void;
	handleClose?: () => void;
	isVisible?: boolean;
	stepDetail?: Guide | null;
	handleNext?: () => void;
}
export interface WelcomeVideoGuideHookProps {
	isWelcomeVideo: boolean;
	completeCurrentStep: () => Promise<void>;
}

export const WelcomeVideoGuide: Guide = {
	id: Steps.WELCOME_VIDEO,
	type: GuideType.VIDEO,
	stageComplete: true,
	triggerAPI: false,
	guideUIProps: {
		fileType: GuideMediaType.VIDEO,
		fileName:
			"https://static.zomentum.com/videos/grow_onboarding/welcome_video.mp4",
		buttonText: "Start Product Tour"
	}
};

export const StageStepsMap: Record<Stages, Guide[]> = {
	//Document Module
	[Stages.DOCUMENT_INTRODUCTION]: [
		{
			id: Steps.HIGHLIGHT_DOCUMENT_MENUITEM,
			type: GuideType.POPUP,
			triggerAPI: true,
			stageComplete: true,
			guideUIProps: {
				title: "Explore Documents",
				description:
					"Create, manage, edit, and send documents quickly for effective/faster client communication and win more deals.",
				placement: "leftTop",
				visible: true,
				closeOnClickOut: false,
				highlightId: "elem_" + Steps.HIGHLIGHT_DOCUMENT_MENUITEM,
				hasTransparentMask: false,
				buttonText: "Next",
				mediaUrl:
					"https://static.zomentum.com/videos/grow_onboarding/explore_document_module.mp4"
			}
		}
		/*{
			id: Steps.SHOW_DOCUMENT_INTRO_VIDEO,
			type: GuideType.VIDEO,
			triggerAPI: true,
			stageComplete: true,
			guideUIProps: {
				fileType: GuideMediaType.VIDEO,
				fileName:
					"https://s3.amazonaws.com/channelforcestage.com/videos/grow_onboarding/vendor_focus_on_the_value.mp4",
				buttonText: "Add Document"
			}
		}*/
	],
	[Stages.ADD_DOCUMENT]: [
		{
			id: Steps.HIGHLIGHT_ADD_DOCUMENT_BUTTON,
			type: GuideType.NORMAL,
			guideUIProps: {
				title: "Create Your First Document",
				description:
					"Use predefined templates, build quotes, and get instant approvals. ",
				placement: "bottom",
				visible: true,
				highlightId: "elem_" + Steps.HIGHLIGHT_ADD_DOCUMENT_BUTTON,
				hasTransparentMask: false,
				closeOnClickOut: false,
				hasArrow: true,
				width: 252
			}
		},
		{
			id: Steps.SHOW_SELECT_TEMPLATE,
			type: GuideType.NORMAL,
			stageComplete: true,
			guideUIProps: {
				title: "Let's Select A Template",
				description:
					"We've already selected the fields for you to get started",
				placement: "top",
				visible: true,
				closeOnClickOut: false,
				hasArrow: true,
				highlightId: "elem_" + Steps.SHOW_SELECT_TEMPLATE,
				hasTransparentMask: true,
				width: 240
			}
		}
	],
	[Stages.SELECTING_TEMPLATE]: [
		{
			id: Steps.HIGHLIGHT_SELECT_TEMPLATE,
			type: GuideType.NORMAL,
			guideUIProps: {
				description:
					"Here you can select a template. Don't worry we have already selected one for you to begin with.",
				placement: "rightTop",
				visible: true,
				highlightId: "elem_" + Steps.HIGHLIGHT_SELECT_TEMPLATE,
				hasTransparentMask: false,
				closeOnClickOut: false,
				buttonText: "Next",
				hasArrow: false,
				width: 248
			}
		},
		{
			id: Steps.SHOW_CREATE_DOCUMENT,
			type: GuideType.NORMAL,
			triggerAPI: true,
			stageComplete: true,
			guideUIProps: {
				description: "Create a document with selected template.",
				placement: "top",
				visible: true,
				closeOnClickOut: false,
				hasArrow: true,
				highlightId: "elem_" + Steps.SHOW_CREATE_DOCUMENT,
				hasTransparentMask: true,
				width: 192
			}
		}
	],
	[Stages.EXPLORE_EDITOR]: [
		{
			id: Steps.SHOW_DRAG_DROP_BLOCK,
			type: GuideType.POPUP,
			isEntityRequired: true,
			guideUIProps: {
				title: "Drag And Drop Blocks To The Editor",
				description:
					"Add text, image, video, and quote blocks to build your document.",
				note: "Try it: Select and drag a block to the editor.",
				placement: "leftTop",
				visible: true,
				closeOnClickOut: false,
				highlightId: "elem_" + Steps.SHOW_DRAG_DROP_BLOCK,
				hasTransparentMask: true,
				buttonText: "",
				mediaUrl:
					"https://static.zomentum.com/videos/grow_onboarding/explore_document_editor.mp4"
			}
		},
		{
			id: Steps.SHOW_BLOCK_SAVE_BLOCK,
			type: GuideType.NORMAL,
			isEntityRequired: true,
			guideUIProps: {
				description:
					"Click on the icon to save this block as a Custom Block",
				placement: "topLeft",
				visible: true,
				closeOnClickOut: false,
				hasArrow: true,
				highlightId: "elem_" + Steps.SHOW_BLOCK_SAVE_BLOCK,
				hasTransparentMask: true,
				buttonText: "Save",
				width: 240
			}
		},
		{
			id: Steps.SHOW_SEND_DOCUMENT_IN_EDITOR,
			type: GuideType.NORMAL,
			isEntityRequired: true,
			guideUIProps: {
				description: "Click here to send the Document",
				placement: "bottom",
				visible: true,
				closeOnClickOut: false,
				hasArrow: true,
				highlightId: "elem_" + Steps.SHOW_SEND_DOCUMENT_IN_EDITOR,
				hasTransparentMask: true,
				width: 172
			}
		},
		{
			id: Steps.SHOW_SEND_EMAIL,
			type: GuideType.NORMAL,
			isEntityRequired: true,
			guideUIProps: {
				description: "Let's send the email",
				placement: "topRight",
				visible: false,
				closeOnClickOut: false,
				hasArrow: true,
				highlightId: "elem_" + Steps.SHOW_SEND_EMAIL,
				hasTransparentMask: true,
				width: 180
			}
		},
		{
			id: Steps.SHOW_GO_BACK_TO_DOCUMENT_LIST,
			type: GuideType.NORMAL,
			stageComplete: true,
			isEntityRequired: true,
			guideUIProps: {
				description:
					"Explore this document to know more features of the editor. Following that, click here to go back to the Document Overview section.",
				placement: "bottom",
				visible: true,
				closeOnClickOut: false,
				hasArrow: true,
				highlightId: "elem_" + Steps.SHOW_GO_BACK_TO_DOCUMENT_LIST,
				hasTransparentMask: true,
				buttonText: "Got It",
				width: 320
			}
		},
		{
			id: Steps.HIGHLIGHT_CREATED_DOCUMENT_IN_LIST,
			type: GuideType.NORMAL,
			moduleComplete: true,
			isEntityRequired: true,
			triggerAPI: true,
			guideUIProps: {
				description: "Access and track all created documents",
				placement: "bottomLeft",
				visible: true,
				closeOnClickOut: true,
				hasArrow: false,
				highlightId: "elem_" + Steps.HIGHLIGHT_CREATED_DOCUMENT_IN_LIST,
				hasTransparentMask: true,
				completeOnClickOut: true,
				buttonText: "Got It",
				width: 284
			}
		}
	],

	//Opportunity Module
	[Stages.OPPORTUNITY_INTRODUCTION]: [
		{
			id: Steps.HIGHLIGHT_OPPORTUNITY_MENUITEM,
			type: GuideType.POPUP,
			triggerAPI: true,
			stageComplete: true,
			guideUIProps: {
				title: "Explore Opportunities",
				description:
					"Get vendor deals, manage leads, and boost pipeline efficiency.",
				placement: "leftTop",
				visible: true,
				closeOnClickOut: false,
				highlightId: "elem_" + Steps.HIGHLIGHT_OPPORTUNITY_MENUITEM,
				hasTransparentMask: false,
				buttonText: "Next",
				mediaUrl:
					"https://static.zomentum.com/videos/grow_onboarding/explore_opportunity_module.mp4"
			}
		}
		/*{
			id: Steps.SHOW_OPPORTUNITY_INTRO_VIDEO,
			type: GuideType.VIDEO,
			triggerAPI: true,
			stageComplete: true,
			guideUIProps: {
				fileType: GuideMediaType.VIDEO,
				fileName:
					"https://s3.amazonaws.com/channelforcestage.com/videos/grow_onboarding/vendor_focus_on_the_value.mp4",
				buttonText: "Add Opportunity"
			}
		}*/
	],
	[Stages.ADD_OPPORTUNITY]: [
		{
			id: Steps.HIGHLIGHT_ADD_OPPORTUNITY_BUTTON,
			type: GuideType.NORMAL,
			guideUIProps: {
				title: "Create Your First Opportunity",
				description:
					"Opportunities are the best way to keep track of the evolution of your deal",
				placement: "bottom",
				visible: true,
				highlightId: `elem_${Steps.HIGHLIGHT_ADD_OPPORTUNITY_BUTTON}`,
				closeOnClickOut: false,
				hasArrow: true,
				width: 262
			}
		},
		{
			id: Steps.HIGHLIGHT_ADD_OPPORTUNITY_DRAWER,
			type: GuideType.NORMAL,
			guideUIProps: {
				description:
					"Continue adding this Opportunity with pre-filled Information",
				placement: "right",
				visible: true,
				highlightId: `elem_${Steps.HIGHLIGHT_ADD_OPPORTUNITY_DRAWER}`,
				closeOnClickOut: false,
				buttonText: "Next",
				hasArrow: true,
				width: 240,
				maskOnClickableArea: true
			}
		},
		{
			id: Steps.HIGHLIGHT_OPPORTUNITY_SHARE_WITH_VENDOR_CHECKBOX,
			type: GuideType.NORMAL,
			guideUIProps: {
				description:
					"Collaborate with your vendor by sharing this Opportunity with them!",
				placement: "leftTop",
				visible: true,
				highlightId: `elem_${Steps.HIGHLIGHT_OPPORTUNITY_SHARE_WITH_VENDOR_CHECKBOX}`,
				closeOnClickOut: false,
				buttonText: "Got It",
				hasArrow: false,
				width: 264,
				maskOnClickableArea: true,
				hasInfoIcon: true
			}
		},
		{
			id: Steps.HIGHLIGHT_OPPORTUNITY_SAVE_BUTTON,
			type: GuideType.NORMAL,
			stageComplete: true,
			triggerAPI: true,
			guideUIProps: {
				description: "Click here to create a new Opportunity.",
				placement: "topRight",
				visible: true,
				highlightId: `elem_${Steps.HIGHLIGHT_OPPORTUNITY_SAVE_BUTTON}`,
				closeOnClickOut: false,
				hasArrow: true,
				width: 162
			}
		}
	],
	[Stages.OPPORTUNITY_ACTIVITIES]: [
		{
			id: Steps.SHOW_OPPORTUNITY_CONGRATULATION_MODAL,
			type: GuideType.POPUP_MODAL,
			stageComplete: true,
			moduleComplete: true,
			triggerAPI: true,
			isEntityRequired: true,
			guideUIProps: {
				title: "Congratulations!",
				confirmText: "Explore",
				mediaUrl:
					"https://static.zomentum.com/videos/grow_onboarding/explore_opportunity_details.mp4",
				description:
					"Great job on creating your first opportunity! Take your time to explore all the activities you need to manage this opportunity"
			}
		}
	],

	//Align Module
	[Stages.ALIGN_INTRODUCTION]: [
		{
			id: Steps.HIGHLIGHT_ALIGN_MENUITEM,
			type: GuideType.POPUP,
			stageComplete: true,
			triggerAPI: true,
			guideUIProps: {
				title: "Explore Resources In Align",
				description:
					"Find enablement content from vendors and manage them all in one place.",
				placement: "leftBottom",
				visible: true,
				highlightId: "elem_" + Steps.HIGHLIGHT_ALIGN_MENUITEM,
				closeOnClickOut: false,
				buttonText: "Next",
				mediaUrl:
					"https://static.zomentum.com/videos/grow_onboarding/explore_align_resources.mp4"
			}
		}
	],
	[Stages.ALIGN_VENDORS_AND_RESOURCES]: [
		{
			id: Steps.HIGHLIGHT_ALIGN_RESOURCES_BUTTON,
			type: GuideType.NORMAL,
			stageComplete: true,
			triggerAPI: true,
			guideUIProps: {
				description: "Checkout your vendor's enablement content.",
				placement: "right",
				visible: true,
				highlightId: "elem_" + Steps.HIGHLIGHT_ALIGN_RESOURCES_BUTTON,
				closeOnClickOut: false,
				hasArrow: true,
				width: 184
			}
		}
	],
	[Stages.ALIGN_EXPLORE_RESOURCES]: [
		{
			id: Steps.HIGHLIGHT_ALIGN_VENDOR_RESOURCE_FOLDER,
			type: GuideType.NORMAL,
			guideUIProps: {
				description:
					"Navigate through the folders to explore the resources.",
				placement: "right",
				visible: true,
				highlightId:
					"elem_" + Steps.HIGHLIGHT_ALIGN_VENDOR_RESOURCE_FOLDER,
				closeOnClickOut: false,
				buttonText: "Next",
				hasArrow: true,
				width: 216
			}
		},
		{
			id: Steps.HIGHLIGHT_ALIGN_VENDOR_RESOURCE_FILE,
			type: GuideType.NORMAL,
			guideUIProps: {
				description: "Access your resources instantly.",
				placement: "right",
				visible: true,
				highlightId:
					"elem_" + Steps.HIGHLIGHT_ALIGN_VENDOR_RESOURCE_FILE,
				closeOnClickOut: false,
				buttonText: "Got It",
				hasArrow: true,
				width: 212
			}
		},
		{
			id: Steps.SHOW_ALIGN_CONGRATULATION_MODAL,
			type: GuideType.POPUP_MODAL,
			stageComplete: true,
			moduleComplete: true,
			triggerAPI: true,
			guideUIProps: {
				illustration: AlignCongratulationsFlash as React.ElementType,
				title: "Great Job!",
				confirmText: "Got It",
				description:
					"You've completed the Align module. You can continue exploring Align or checkout other modules."
			}
		}
	],

	// Integration module
	[Stages.INTEGRATION_SELECT_APP]: [],
	[Stages.INTEGRATION_INTRODUCTION]: [],
	[Stages.INTEGRATION_APPS]: []
};
